import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { TrackedLink } from '../TrackedLink';
import { ResponsiveImage } from '../ResponsiveImage';
import { StyledCtaButton, CtaImageContainer } from './styles.js';
import { ctaButtonClicked } from './events';

export const CtaButton = ({
  children,
  href,
  variant = 'primary',
  onClick,
  fullWidth = false,
  openNewTab = false,
  minWidth = 240,
  headerText = '',
  alignment = 'center',
  icon = null,
  iconAlignment = 'left',
}) => {
  const { formattedAmount } = useShopifyOfferContext();

  const getCtaPosition = href => {
    const params = new URLSearchParams(href.split('?')[1]);
    return params.get('cta_position');
  };

  const ctaPosition = getCtaPosition(href);
  const text = typeof children === 'string' ? children : null;
  const eventPayload = ctaButtonClicked(ctaPosition, href, text, headerText);

  const iconNode = icon ? <ResponsiveImage {...icon} /> : null;

  return (
    // Would it make sense for CTA button to just extend LinkButton rather than wrap it?
    <StyledCtaButton $alignment={alignment}>
      <TrackedLink
        href={href}
        variant={variant}
        horizontalPadding={6}
        verticalPadding={0}
        minWidth={minWidth}
        onClick={onClick}
        fullWidth={fullWidth}
        target={openNewTab ? '_blank' : null}
        rel={openNewTab ? 'noopener noreferrer' : null}
        headerText={headerText}
        analyticsEvent={eventPayload}
      >
        {iconAlignment === 'left' && iconNode && (
          <CtaImageContainer $position="left">{iconNode}</CtaImageContainer>
        )}
        {typeof children === 'string'
          ? interpolate(children, { offer: formattedAmount ?? ' 50%' })
          : children}
        {iconAlignment === 'right' && iconNode && (
          <CtaImageContainer $position="right">{iconNode}</CtaImageContainer>
        )}
      </TrackedLink>
    </StyledCtaButton>
  );
};

CtaButton.displayName = 'CtaButton';

CtaButton.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  fullWidth: PropTypes.bool,
  openNewTab: PropTypes.bool,
  headerText: PropTypes.string,
  icon: PropTypes.shape({
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    alt: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  iconAlignment: PropTypes.oneOf(['left', 'right']),
};
